import {apiFetch} from '../../../shared/apiFetch';
import {AI_DEFAULTS, API_CONFIG, ERROR_MESSAGES} from '../../config';

const stripCodeFences = (raw) => raw.replace(/```json|```/gi, '').trim();

const normalizeInputImages = (inputImages = []) =>
    inputImages
        .map((image) => ({
            url: typeof image === 'string' ? image : image?.url,
            name: typeof image === 'string' ? '' : image?.name || '',
        }))
        .filter((image) => typeof image.url === 'string' && image.url.trim());

const collectBalancedJsonCandidates = (raw) => {
    const candidates = [];
    let startIndex = -1;
    let depth = 0;
    let inString = false;
    let isEscaped = false;

    for (let index = 0; index < raw.length; index += 1) {
        const char = raw[index];

        if (isEscaped) {
            isEscaped = false;
            continue;
        }

        if (char === '\\') {
            isEscaped = true;
            continue;
        }

        if (char === '"') {
            inString = !inString;
            continue;
        }

        if (inString) continue;

        if (char === '{') {
            if (depth === 0) startIndex = index;
            depth += 1;
        } else if (char === '}') {
            depth -= 1;
            if (depth === 0 && startIndex !== -1) {
                candidates.push(raw.slice(startIndex, index + 1));
                startIndex = -1;
            }
        }
    }

    return candidates;
};

const RETRYABLE_STATUS_CODES = new Set([429, 500, 502, 503, 504]);
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000;

// ── FAQ answer opening patterns ────────────────────────────────────────
// Used to enforce variety across FAQ answers. Each pattern has:
//   - id: short identifier passed into the prompt
//   - label: human-friendly name used in the prompt instruction
//   - example: a model sentence the AI can mimic
//   - matches(text): heuristic detector that returns true if `text`'s
//     opening looks like this pattern (used to count what's already
//     present so the picker can favor under-used patterns).
//
// Detection runs on the FAQ answer's first sentence with template
// tokens left in place — patterns are intentionally permissive so
// minor wording drift still maps to the right bucket.
const FAQ_OPENING_PATTERNS = [
    {
        id: 'FACT_FIRST',
        label: 'FACT-FIRST',
        example:
            'With 3/8-inch tempered glass and stainless-steel hardware, the {{vendor}} {{custom_collection_name}} resists daily wear.',
        matches: (text) => /^\s*(?:with|featuring|including)\b/i.test(text),
    },
    {
        id: 'USE_CASE_FIRST',
        label: 'USE-CASE-FIRST',
        example:
            'For openings 44–48 inches wide, the {{vendor}} {{custom_collection_name}} adjusts to fit precisely.',
        matches: (text) => /^\s*(?:for|in|on|to fit|to handle|to suit)\b/i.test(text),
    },
    {
        id: 'ATTRIBUTE_FIRST',
        label: 'ATTRIBUTE-FIRST',
        example:
            'Stainless-steel hardware on the {{vendor}} {{custom_collection_name}} resists corrosion in humid bathrooms.',
        matches: (text) =>
            /^\s*(?:stainless|brass|aluminum|chrome|nickel|tempered|frameless|semi-frameless|solid|premium)\b/i.test(
                text,
            ),
    },
    {
        id: 'COMPARATIVE_FIRST',
        label: 'COMPARATIVE-FIRST',
        example:
            'Unlike framed alternatives, the {{vendor}} {{custom_collection_name}} eliminates metal seams that trap grime.',
        matches: (text) =>
            /^\s*(?:unlike|compared to|versus|rather than|instead of)\b/i.test(text),
    },
    {
        id: 'CONDITION_FIRST',
        label: 'CONDITION-FIRST',
        example:
            'When installed in a standard alcove, the {{vendor}} {{custom_collection_name}} mounts without extra adapters.',
        matches: (text) =>
            /^\s*(?:when|once|after|before|if)\b/i.test(text),
    },
    {
        id: 'CERTIFICATION_FIRST',
        label: 'CERTIFICATION-FIRST',
        example:
            'Certified to ANSI Z97.1 and 16 CFR 1201, the {{vendor}} {{custom_collection_name}} meets residential safety standards.',
        matches: (text) =>
            /^\s*(?:certified|tested|rated|approved|listed)\b/i.test(text),
    },
    {
        id: 'BUYER_CONCERN_FIRST',
        label: 'BUYER-CONCERN-FIRST',
        example:
            'Maintenance is straightforward on the {{vendor}} {{custom_collection_name}} thanks to its frameless build and clear glass.',
        matches: (text) =>
            /^\s*(?:maintenance|installation|cleaning|care|warranty coverage|shipping|delivery|warranty)\b/i.test(
                text,
            ),
    },
    {
        id: 'BRAND_FIRST',
        label: 'BRAND-FIRST (use sparingly)',
        example:
            'The {{vendor}} {{custom_collection_name}} uses 3/8-inch tempered glass for daily impact resistance.',
        // Match last — most permissive (text starts with "The …").
        matches: (text) =>
            /^\s*(?:the\s+\{\{|the\s+[a-z][a-z'-]+\s+[a-z])/i.test(text),
    },
];

const FAQ_PATTERN_BY_ID = new Map(
    FAQ_OPENING_PATTERNS.map((pattern) => [pattern.id, pattern]),
);

// Returns the FAQ_OPENING_PATTERNS.id that best matches `text`, or null
// if nothing matches (e.g. answer doesn't follow any recognized opening).
export function detectFaqOpeningPattern(text = '') {
    if (typeof text !== 'string' || !text.trim()) return null;
    // Trim leading quote / bracket / pronoun-cleanup so detection works
    // on the first content word.
    const cleaned = text.trim();
    for (const pattern of FAQ_OPENING_PATTERNS) {
        if (pattern.matches(cleaned)) return pattern.id;
    }
    return null;
}

// Given an array of existing FAQ answers, count opening patterns and
// return the id of the least-used pattern. BRAND-FIRST is held in
// reserve and only picked if every other pattern is at least as common.
export function pickNextFaqOpening(existingAnswers = []) {
    const counts = Object.fromEntries(
        FAQ_OPENING_PATTERNS.map((pattern) => [pattern.id, 0]),
    );
    existingAnswers.forEach((answer) => {
        const id = detectFaqOpeningPattern(answer);
        if (id) counts[id] += 1;
    });

    const nonBrand = FAQ_OPENING_PATTERNS.filter(
        (pattern) => pattern.id !== 'BRAND_FIRST',
    );
    const minNonBrand = Math.min(
        ...nonBrand.map((pattern) => counts[pattern.id]),
    );
    const candidates = nonBrand.filter(
        (pattern) => counts[pattern.id] === minNonBrand,
    );

    // Only fall back to BRAND_FIRST when every other pattern already has
    // at least one use — that keeps it rare without banning it outright.
    if (counts.BRAND_FIRST < minNonBrand) {
        candidates.push(FAQ_PATTERN_BY_ID.get('BRAND_FIRST'));
    }

    if (!candidates.length) return 'FACT_FIRST';
    return candidates[Math.floor(Math.random() * candidates.length)].id;
}

class AIService {
    constructor() {
        this.timeout = API_CONFIG.TIMEOUT;
    }

    createTimeoutController(timeoutMs = this.timeout) {
        if (typeof AbortController === 'undefined') return null;

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        return {
            signal: controller.signal,
            cancel: () => clearTimeout(timeoutId),
        };
    }

    buildUserMessageContent(prompt, inputImages = []) {
        const resolvedPrompt = typeof prompt === 'string' ? prompt.trim() : '';
        const resolvedImages = normalizeInputImages(inputImages);

        if (!resolvedImages.length) return resolvedPrompt;

        return [
            ...(resolvedPrompt ? [{type: 'text', text: resolvedPrompt}] : []),
            ...resolvedImages.map((image) => ({
                type: 'image_url',
                image_url: {url: image.url},
            })),
        ];
    }

    extractJsonObject(raw) {
        const cleaned = stripCodeFences(raw || '');
        const candidates = [cleaned];
        const firstBrace = cleaned.indexOf('{');
        const lastBrace = cleaned.lastIndexOf('}');

        if (firstBrace !== -1 && lastBrace > firstBrace) {
            candidates.push(cleaned.slice(firstBrace, lastBrace + 1));
        }

        candidates.push(...collectBalancedJsonCandidates(cleaned));

        for (const candidate of candidates) {
            if (!candidate) continue;
            try {
                return JSON.parse(candidate);
            } catch {
                // Try the next candidate.
            }
        }

        throw new Error('The model response was not valid JSON.');
    }

    normalizeGeneratedResult(rawResult) {
        return {
            title: typeof rawResult?.title === 'string' ? rawResult.title.trim() : '',
            desc: typeof rawResult?.desc === 'string' ? rawResult.desc.trim() : '',
            bullets: Array.isArray(rawResult?.bullets)
                ? rawResult.bullets
                      .map((bullet) => ({
                          field: typeof bullet?.field === 'string' ? bullet.field.trim() : '',
                          text: typeof bullet?.text === 'string' ? bullet.text.trim() : '',
                      }))
                      .filter((bullet) => bullet.field || bullet.text)
                : [],
            faqs: Array.isArray(rawResult?.faqs)
                ? rawResult.faqs
                      .map((faq) => ({
                          question:
                              typeof faq?.question === 'string' ? faq.question.trim() : '',
                          answer:
                              typeof faq?.answer === 'string' ? faq.answer.trim() : '',
                      }))
                      .filter((faq) => faq.question && faq.answer)
                : [],
        };
    }

    normalizeReviewDescriptionResult(rawResult) {
        return {
            description:
                typeof rawResult?.description === 'string' ? rawResult.description.trim() : '',
        };
    }

    extractMessageText(data) {
        const content = data?.choices?.[0]?.message?.content;

        if (typeof content === 'string') {
            return content;
        }

        if (Array.isArray(content)) {
            return content
                .map((block) => {
                    if (typeof block === 'string') return block;
                    if (block?.type === 'text' && typeof block?.text === 'string') {
                        return block.text;
                    }
                    if (block?.type === 'text' && typeof block?.text?.value === 'string') {
                        return block.text.value;
                    }
                    return '';
                })
                .filter(Boolean)
                .join('\n');
        }

        return '';
    }

    createFallbackGeneratedResult({resolvedTopic, resolvedRows, enabled, faqCount = 4}) {
        const topicLabel = resolvedTopic || 'Lorem Ipsum';
        const title = enabled.title ? `Lorem Ipsum ${topicLabel}`.trim().slice(0, 60) : '';
        const desc = enabled.desc
            ? 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam.'
            : '';
        const bullets = enabled.bullets
            ? resolvedRows.map((row, index) => ({
                  field: row.field || `Bullet ${index + 1}`,
                  text:
                      index % 2 === 0
                          ? 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.'
                          : 'Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
              }))
            : [];
        const faqs = enabled.faqs
            ? Array.from({length: Math.max(1, faqCount)}, (_unused, index) => ({
                  question: `Lorem ipsum question ${index + 1}?`,
                  answer:
                      'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
              }))
            : [];

        return this.normalizeGeneratedResult({
            title,
            desc,
            bullets,
            faqs,
        });
    }

    createFallbackReviewDescription({listingContext, charLimit = 0}) {
        const productLabel = listingContext?.name || listingContext?.title || 'This product';
        const detailBits = [
            listingContext?.category,
            listingContext?.finish,
            listingContext?.material,
        ].filter(Boolean);
        const detailLine = detailBits.length ? ` in ${detailBits.join(', ')}` : '';
        const bulletSummary = Array.isArray(listingContext?.bullets)
            ? listingContext.bullets
                  .map((bullet) => bullet.text || bullet.label)
                  .filter(Boolean)
                  .slice(0, 2)
                  .join(' ')
            : '';
        const keywordSummary = Array.isArray(listingContext?.searchTerms)
            ? listingContext.searchTerms.slice(0, 3).join(', ')
            : '';

        let description = `${productLabel}${detailLine} is designed for polished product copy.`;
        if (bulletSummary) {
            description += ` Highlights include ${bulletSummary.toLowerCase()}.`;
        }
        if (keywordSummary) {
            description += ` Natural search language can include ${keywordSummary}.`;
        }
        if (charLimit > 0) {
            description = description.slice(0, charLimit).trim();
        }

        return this.normalizeReviewDescriptionResult({description});
    }

    buildProfileRequirements({profileSettings, enabled}) {
        if (!profileSettings) return '';

        const buildSectionRequirements = (label, sectionSettings, extraDirectives = []) => {
            if (!sectionSettings) return '';

            const directives = [...extraDirectives];

            if (sectionSettings.minChars && sectionSettings.maxChars) {
                directives.push(
                    `Keep it between ${sectionSettings.minChars} and ${sectionSettings.maxChars} characters.`,
                );
            } else if (sectionSettings.maxChars) {
                directives.push(`Keep it under ${sectionSettings.maxChars} characters.`);
            } else if (sectionSettings.minChars) {
                directives.push(`Keep it above ${sectionSettings.minChars} characters.`);
            }

            if (sectionSettings.defaultTone) {
                directives.push(`Use a ${sectionSettings.defaultTone} tone.`);
            }

            if (sectionSettings.defaultWritingStyle) {
                directives.push(`Use a ${sectionSettings.defaultWritingStyle} writing style.`);
            }

            if (sectionSettings.profileContext) {
                directives.push(`Additional context: ${sectionSettings.profileContext}`);
            }

            return directives.length ? `- ${label}: ${directives.join(' ')}` : '';
        };

        const requirements = [];

        if (enabled.title) {
            requirements.push(
                buildSectionRequirements('Title', profileSettings.title),
            );
        }

        if (enabled.desc) {
            requirements.push(
                buildSectionRequirements('Description', profileSettings.description),
            );
        }

        if (enabled.bullets) {
            const bulletDirectives = [];

            if (profileSettings.bullets?.headingStyle === 'allCaps') {
                bulletDirectives.push(
                    'Start each bullet text with a short ALL CAPS heading followed by a colon.',
                );
            } else if (profileSettings.bullets?.headingStyle === 'sentenceCase') {
                bulletDirectives.push(
                    'Start each bullet text with a short sentence-case heading followed by a colon.',
                );
            } else if (profileSettings.bullets?.headingStyle === 'none') {
                bulletDirectives.push('Do not add an extra heading inside each bullet text.');
            }
            bulletDirectives.push(
                'Avoid repeating the exact product name in bullet text unless the bullet field or keywords explicitly require it. Prefer product type, category, finish, material, or use-case language instead.',
            );

            requirements.push(
                buildSectionRequirements('Bullets', profileSettings.bullets, bulletDirectives),
            );
        }

        if (enabled.faqs && profileSettings.faqs) {
            const faqs = profileSettings.faqs;
            const faqDirectives = [];

            if (faqs.minQuestionChars && faqs.maxQuestionChars) {
                faqDirectives.push(
                    `Each question must be between ${faqs.minQuestionChars} and ${faqs.maxQuestionChars} characters.`,
                );
            } else if (faqs.maxQuestionChars) {
                faqDirectives.push(`Each question must be under ${faqs.maxQuestionChars} characters.`);
            }

            if (faqs.minAnswerChars && faqs.maxAnswerChars) {
                faqDirectives.push(
                    `Each answer must be between ${faqs.minAnswerChars} and ${faqs.maxAnswerChars} characters.`,
                );
            } else if (faqs.maxAnswerChars) {
                faqDirectives.push(`Each answer must be under ${faqs.maxAnswerChars} characters.`);
            }

            if (faqs.defaultTone) faqDirectives.push(`Use a ${faqs.defaultTone} tone.`);
            if (faqs.defaultWritingStyle) {
                faqDirectives.push(`Use a ${faqs.defaultWritingStyle} writing style.`);
            }
            if (faqs.profileContext) {
                faqDirectives.push(`Additional context: ${faqs.profileContext}`);
            }

            const section = faqDirectives.length
                ? `- FAQs: ${faqDirectives.join(' ')}`
                : '';
            if (section) requirements.push(section);
        }

        const content = requirements.filter(Boolean).join('\n');
        return content ? `\n\nAdditional profile requirements:\n${content}` : '';
    }

    buildPrompt({
        resolvedTopic,
        tone,
        usecase,
        audience,
        enabled,
        resolvedRows,
        profileSettings,
        descriptionContext = '',
        referenceImageCount = 0,
        vars = [],
        brandContext = '',
        // For single-FAQ regenerate: a pre-selected opening pattern id
        // (e.g. "FACT_FIRST") that the caller picked client-side by
        // scanning sibling FAQs and choosing the least-used pattern.
        // When set, the prompt locks the FAQ to that pattern instead
        // of asking the model to rotate freely. Null/empty for batch
        // generation where the model gets the full gallery and rotates
        // within its own multi-FAQ response.
        openingPattern = null,
    }) {
        const fieldList = resolvedRows
            .map(
                (row, index) =>
                    `${index + 1}. Field: "${row.field || 'general'}" | Keywords: ${row.kw || 'none'}`,
            )
            .join('\n');
        const profileRequirements = this.buildProfileRequirements({profileSettings, enabled});
        // Description context is treated as MANDATORY content. Earlier
        // wording ("Description-specific direction") let the AI cherry-pick
        // which values to mention — natural-sounding ones survived,
        // technical ones (barcodes, variant labels) got dropped. The
        // stronger phrasing below tells the model to incorporate every
        // value while still allowing natural integration.
        const descriptionContextBlock =
            enabled.desc && descriptionContext
                ? `\n\nDescription must incorporate ALL of the following details — work them into the copy naturally, but do not omit, paraphrase, or summarise any of them:\n${descriptionContext}`
                : '';
        const imageReferenceBlock = referenceImageCount
            ? `\n\nVisual references:\n- ${referenceImageCount} product image${referenceImageCount === 1 ? '' : 's'} ${referenceImageCount === 1 ? 'is' : 'are'} attached to this request.\n- Use them to ground visible attributes like finish, shape, silhouette, and hardware details.\n- Do not invent specifications or hidden features that are not visible in the images.`
            : '';

        // Build product attributes block so AI understands what each value means
        const attributesBlock = vars.length
            ? `\n\nProduct attributes (attribute name → value):\n${vars.map((v) => `- ${v.name.replace(/_/g, ' ')}: ${v.sample}`).join('\n')}\n\nUse these attributes to write accurate, specific content. Each attribute name describes what the value represents.`
            : '';

        // Features rows drive FAQ generation 1:1 when present — each row
        // becomes one FAQ, with the row's field as the topic and its
        // keywords as the answer context. If no rows are configured, fall
        // back to a free-form count from profile settings so FAQs can be
        
        // generated standalone.
        const faqRowDriven = enabled.faqs && resolvedRows.length > 0;
        const faqCount = faqRowDriven
            ? resolvedRows.length
            : profileSettings?.faqs?.faqCount || 4;
        const faqFieldList = faqRowDriven
            ? resolvedRows
                  .map(
                      (row, index) =>
                          `${index + 1}. Topic: "${row.field || 'general'}" | Answer context: ${row.kw || 'none'}`,
                  )
                  .join('\n')
            : '';

        // Locked opening pattern (single-FAQ regenerate case). The
        // caller selected this client-side by scanning sibling FAQs.
        // When set, we override the "rotate within the gallery" rule
        // with a hard "use THIS pattern" directive — zero tokens spent
        // shipping sibling content, deterministic variety.
        const forcedOpening =
            enabled.faqs && openingPattern && FAQ_PATTERN_BY_ID.has(openingPattern)
                ? FAQ_PATTERN_BY_ID.get(openingPattern)
                : null;
        const forcedOpeningBlock = forcedOpening
            ? `\n\nFORCED OPENING PATTERN for this FAQ (overrides the rotation rule above):\n- Use the ${forcedOpening.label} pattern.\n- Example: ${forcedOpening.example}\n- The entity mention rule still applies — "{{vendor}} {{custom_collection_name}}" must appear somewhere in the first sentence.`
            : '';

        // FAQ ATTRIBUTE SCOPE — restrict FAQ answers to ONLY the
        // attributes explicitly referenced via {{var}} in each FAQ
        // topic's keywords or field. Always-allowed: vendor and
        // custom_collection_name (used for the canonical product
        // reference). Without this, the model pulls in unrelated
        // attribute values from the broader Product attributes block,
        // description context, or other rows' bullet keywords — which
        // is the exact behavior the user wants suppressed.
        const faqAllowedVarNames = enabled.faqs
            ? Array.from(
                  new Set([
                      'vendor',
                      'custom_collection_name',
                      ...resolvedRows.flatMap((row) => row.referencedVarNames || []),
                  ]),
              )
            : [];
        const faqRowScopeLines = enabled.faqs && faqRowDriven
            ? resolvedRows
                  .map((row, index) => {
                      const names = row.referencedVarNames || [];
                      const list = names.length
                          ? names.map((name) => `{{${name}}}`).join(', ')
                          : '(none — use only literal facts from the topic keywords)';
                      return `${index + 1}. "${row.field || 'general'}" → allowed attributes: ${list}`;
                  })
                  .join('\n')
            : '';
        const faqAttributeScopeBlock = enabled.faqs && faqAllowedVarNames.length
            ? `\n\nFAQ ATTRIBUTE SCOPE (strict — overrides the broader Product attributes block above for FAQ output only):
For FAQ questions and answers, you may ONLY reference these attributes as {{attribute_name}} tokens or factual claims:
${faqAllowedVarNames.map((name) => `- {{${name}}}`).join('\n')}

Do NOT, inside FAQs, pull values from:
- Other attributes listed in the Product attributes block above
- The Description context block
- Bullet field keywords from rows other than this FAQ's own topic
- Bullet text from other rows

Each FAQ topic carries its own narrow attribute scope below. Stay within that scope; the broader attributes block is for title/description/bullets, not FAQs.${faqRowScopeLines ? `\n\nPer-topic FAQ attribute scope:\n${faqRowScopeLines}` : ''}`
            : '';
        // GEO/SEO baked-in rules for FAQs. These structural rules are
        // non-negotiable because they're what distinguishes a Q&A that
        // ranks / gets cited by LLMs from a generic one. Character limits,
        // tone, and writing style remain configurable via the brand
        // profile and flow through `profileRequirements`.
        const faqGeoRules = enabled.faqs
            ? `\n\nFAQ generation rules (mandatory) — write for human shoppers AND AI answer engines (ChatGPT, Google AI Overviews, Perplexity):

PRODUCT REFERENCE (read this first — the most common mistake):
- When you name the product, use ONLY "{{vendor}} {{custom_collection_name}}" (brand + collection line). That's the canonical two-token form.
- DO NOT use the product_name attribute. DO NOT append product type, category, descriptor, or product-line phrases like "Door Handle", "Sliding Shower Door", "Frameless Sliding Shower Door", "Dummy Door Handle", "Lever", "Faucet", "Tempered Glass", etc. after the vendor + collection.

Examples (assume vendor=VIGO, custom_collection_name=Elan E-Class, product_name="VIGO Elan E-Class Frameless Sliding Shower Door"):
- WRONG: "The VIGO Elan E-Class Frameless Sliding Shower Door uses 3/8-inch tempered glass."
- WRONG: "The VIGO Elan E-Class Sliding Shower Door is suitable for…"
- WRONG: "The Elan E-Class Frameless Sliding Shower Door includes…"
- RIGHT: "The {{vendor}} {{custom_collection_name}} uses 3/8-inch tempered glass."

If the attributes block lacks vendor or custom_collection_name, fall back to the shortest unambiguous identifier present in the attributes (in order of preference): {{custom_collection_name}} alone, {{vendor}} alone, or — only as a last resort — the product_name attribute.

ENTITY MENTION & OPENING VARIETY (don't let every FAQ feel templated):
- Every answer MUST mention "{{vendor}} {{custom_collection_name}}" at least once — required for answer-engine extraction so each Q&A makes sense if quoted in isolation.
- DO NOT lead every answer with "The {{vendor}} {{custom_collection_name}}…". Rotate openings across the batch. Out of ${faqCount} FAQs, AT MOST ${Math.max(1, Math.floor(faqCount / 3))} may use the BRAND-FIRST opening; the rest must use one of the other patterns below.
- The entity can appear anywhere in the first sentence — not always as the literal first words. After it appears, pronouns ("it", "they", "this lever") are fine for follow-up sentences in the same answer.
- DO NOT open with bare "Yes" / "No". DO NOT open with "It…" / "This…" if the entity hasn't been named yet in that answer.

Opening pattern gallery — use a different one for each answer in the batch:
- BRAND-FIRST (use sparingly): "The {{vendor}} {{custom_collection_name}} uses 3/8-inch tempered glass for daily impact resistance."
- FACT-FIRST: "With 3/8-inch tempered glass and stainless-steel hardware, the {{vendor}} {{custom_collection_name}} resists daily wear."
- USE-CASE-FIRST: "For openings 44–48 inches wide, the {{vendor}} {{custom_collection_name}} adjusts to fit precisely."
- ATTRIBUTE-FIRST: "Stainless-steel hardware on the {{vendor}} {{custom_collection_name}} resists corrosion in humid bathrooms."
- COMPARATIVE-FIRST: "Unlike framed alternatives, the {{vendor}} {{custom_collection_name}} eliminates metal seams that trap grime."
- CONDITION-FIRST: "When installed in a standard alcove, the {{vendor}} {{custom_collection_name}} mounts without extra adapters."
- CERTIFICATION-FIRST: "Certified to ANSI Z97.1 and 16 CFR 1201, the {{vendor}} {{custom_collection_name}} meets residential safety standards."
- BUYER-CONCERN-FIRST: "Maintenance is straightforward on the {{vendor}} {{custom_collection_name}} thanks to its frameless build and clear glass."

ACCURACY:
- Use ONLY facts confirmed by the product attributes, topic/answer context, description context, or images in this prompt. Never invent specs, model names, certifications, standards, trademarks, materials, finishes, dimensions, weights, warranty terms, included hardware, lockability, or compatibility.
- Reproduce certification/standard names and trademarked feature names EXACTLY as the source provides them (e.g. "ANSI Z97.1" not "Z971"; preserve ™/® if present). If unsure of the exact designation, omit the claim rather than approximate it.
- Check each question's premise for category correctness. Don't assert relationships that aren't true (e.g. a shower door doesn't connect to plumbing; a dummy handle has no latch mechanism).
- For handedness/orientation, answer only for this exact SKU/variant. Do not imply reversibility unless the product data confirms it.

TEMPLATE VARIABLES (critical for multi-variant groups):
- This content is reused across every SKU in the variant group. Variant-specific values (size, finish, color, material, dimensions, weight, finish_family, glass thickness, etc.) WILL differ per SKU — so when you need one of those values in a question or answer, output the attribute as a {{attribute_name}} token, NOT the resolved literal value.
- The values shown in the "Product attributes" block above are this variant's CURRENT resolved values — use them as context for what each attribute means, not as text to paste into the output.
- Examples (assuming attributes include custom_finish, size, custom_material):
  - WRONG: "The handle features a Brushed Nickel finish."
  - RIGHT: "The handle features a {{custom_finish}} finish."
  - WRONG: 'Available in 44" - 48" widths.'
  - RIGHT: "Available in {{size}} widths."
- Truly product-wide values (brand name, product line, certifications that apply to ALL variants) can be written as literal text, since they don't differ per variant.
- Do not invent attribute names. Only use {{attribute_name}} forms where the exact name appears in the attributes block.

QUESTIONS:
- Generate exactly ${faqCount} FAQs${faqRowDriven ? ' — one per topic listed below, in order' : ''}.
- Phrase questions the way real shoppers actually search — natural, conversational, long-tail. Use "How", "What", "Can", "Does", "Is", "When" as natural openers.
- Do NOT repeat the {{vendor}} {{custom_collection_name}} reference in every question — that reads as keyword stuffing to both search engines and answer engines. Name the line in at most 1–2 questions for entity clarity; phrase the rest naturally ("How heavy is it?", "Will it fit my space?", "What's the warranty?", "How do I clean it?").
- Cover the intents real shoppers have for this category — typically sizing/fit, how to measure, materials and durability, care and maintenance, installation and compatibility, weight, warranty, shipping, what makes it different, and residential vs. commercial use. Adapt to the actual product.
- No two questions should be near-duplicates.
- Do not duplicate content already covered by the title, description, or bullets — answer what they don't.

ANSWERS (optimized for GEO / answer-engine extraction):
- Self-containment is enforced by the ENTITY MENTION rule above — the product reference goes somewhere in the first sentence so the answer makes sense if extracted alone.
- Be definitive. Never let filler like "check vendor specifics", "timelines may vary", "contact us for details" stand in as the whole answer — give a real, actionable response built from the supplied facts.
- Lead with concrete, extractable facts: exact dimensions, materials, weight, certifications, warranty terms. Specific numbers are what answer engines cite.
- Keep each answer to 2–3 tight sentences. Cut generic adjectives ("high-quality", "amazing", "premium", "great value", "top-tier") and replace them with specifics.
- Don't open with bare "Yes" or "No" — expand immediately with the reason.
- Write for a person first; do not keyword-stuff.${faqRowDriven ? `\n\nFAQ topics (one Q&A each, in order):\n${faqFieldList}` : ''}`
            : '';

        // Build the JSON schema lines as an array so disabled fields
        // produce no blank lines in the final prompt — keeps the prompt
        // tight and avoids confusing the model.
        const jsonFieldLines = [
            enabled.title
                ? '- "title": one compelling overall headline, under 10 words'
                : null,
            enabled.desc ? '- "desc": one engaging 2-3 sentence description' : null,
            enabled.bullets
                ? `- "bullets": array of ${resolvedRows.length} objects: { "field": "<exact name>", "text": "<punchy 1-sentence bullet>" }`
                : null,
            enabled.faqs
                ? `- "faqs": array of ${faqCount} objects: { "question": "<natural-language question>", "answer": "<answer-first response>" }`
                : null,
        ]
            .filter(Boolean)
            .join('\n');

        const sections = [
            `You are an expert ecommerce copywriter. Generate content for product: ${resolvedTopic || 'the product'}`,
            `Tone: ${tone} | Use case: ${usecase} | Audience: ${audience || 'general audience'}${brandContext}`,
            '',
            'Return ONLY a single JSON object (no markdown, no backticks) with:',
            jsonFieldLines,
            enabled.bullets ? `\nBullet fields:\n${fieldList}` : '',
            attributesBlock,
            descriptionContextBlock,
            imageReferenceBlock,
            faqGeoRules,
            faqAttributeScopeBlock,
            forcedOpeningBlock,
            profileRequirements,
            '',
            'Weave keywords naturally. No filler. Only include the keys listed above.',
        ].filter((section) => section !== null && section !== undefined);

        return sections.join('\n');
    }

    buildReviewDescriptionPrompt({
        listingContext,
        charLimit = 0,
        profileSettings = null,
        referenceImageCount = 0,
    }) {
        const effectiveCharLimit =
            charLimit > 0 ? charLimit : profileSettings?.description?.maxChars || 0;
        const profileRequirements = this.buildProfileRequirements({
            profileSettings,
            enabled: {
                title: false,
                desc: true,
                bullets: false,
            },
        });
        const titleLine = listingContext?.title ? `Current title: ${listingContext.title}` : '';
        const existingDescription = listingContext?.description
            ? `Current description: ${listingContext.description}`
            : 'Current description: none';
        const bulletLines = Array.isArray(listingContext?.bullets) && listingContext.bullets.length
            ? listingContext.bullets
                  .map((bullet, index) => `${index + 1}. ${bullet.label}: ${bullet.text}`)
                  .join('\n')
            : 'No bullet fields provided.';
        const searchTerms = Array.isArray(listingContext?.searchTerms) && listingContext.searchTerms.length
            ? listingContext.searchTerms.join(', ')
            : 'none';
        const imageReferenceBlock = referenceImageCount
            ? `\nVisual references: ${referenceImageCount} product image${referenceImageCount === 1 ? '' : 's'} attached. Use them only to ground visible details such as finish, silhouette, installation style, and hardware form.`
            : '';

        return `You are an expert ecommerce content editor improving a product listing description.
Return ONLY a single JSON object with this shape: {"description":"..."}.

Product: ${listingContext?.name || 'Unknown product'}
SKU: ${listingContext?.sku || 'n/a'}
Category: ${listingContext?.category || 'n/a'}
Variant group: ${listingContext?.superSku || 'standalone'}
${titleLine}
${existingDescription}
Search terms: ${searchTerms}
${effectiveCharLimit > 0 ? `Character limit: ${effectiveCharLimit}` : 'Character limit: none'}
${imageReferenceBlock}

Bullet context:
${bulletLines}

Write one polished product description that:
- sounds premium and specific
- uses the listing context faithfully
- naturally incorporates search terms only when they fit
- avoids filler and keyword stuffing
- stays close to the requested character limit when provided${profileRequirements}`;
    }

    async requestMessage({
        systemPrompt,
        messages = [],
        model = AI_DEFAULTS.model,
        maxTokens = AI_DEFAULTS.maxTokens,
        temperature = AI_DEFAULTS.temperature,
        signal,
    }) {
        let lastError = null;

        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
            if (signal?.aborted) throw new Error('Request was cancelled.');

            const timeoutController = this.createTimeoutController();
            // Link external signal to abort the timeout-based controller
            const onExternalAbort = () => timeoutController?.signal && timeoutController.cancel();
            signal?.addEventListener('abort', onExternalAbort, {once: true});

            try {
                const response = await apiFetch(`${API_CONFIG.PROXY_URL}/ai/message`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        model,
                        maxTokens,
                        temperature,
                        systemPrompt,
                        messages,
                    }),
                    signal: signal || timeoutController?.signal,
                });

                const data = await response.json().catch(() => ({}));
                if (!response.ok) {
                    const message = data.error || data.message || ERROR_MESSAGES.AI_GENERATION_FAILED;
                    const error = new Error(message);
                    error.retryable = RETRYABLE_STATUS_CODES.has(response.status);

                    if (
                        error.retryable &&
                        attempt < MAX_RETRIES &&
                        !message.includes('not configured')
                    ) {
                        const retryAfter = response.headers.get('retry-after');
                        const delay = retryAfter
                            ? Math.min(Number(retryAfter) * 1000, 30000)
                            : INITIAL_RETRY_DELAY * Math.pow(2, attempt);
                        lastError = new Error(
                            response.status === 429
                                ? 'Rate limited by AI provider. Retrying...'
                                : `Server error (${response.status}). Retrying...`,
                        );
                        await new Promise((resolve) => setTimeout(resolve, delay));
                        continue;
                    }

                    throw error;
                }

                return typeof data.content === 'string'
                    ? data.content
                    : this.extractMessageText(data);
            } catch (error) {
                if (signal?.aborted) throw new Error('Request was cancelled.');
                if (error.name === 'AbortError') {
                    throw new Error('The AI request timed out. Try again or check your network connection.');
                }
                lastError = error instanceof Error ? error : new Error(ERROR_MESSAGES.AI_GENERATION_FAILED);
                if (
                    attempt < MAX_RETRIES &&
                    error.retryable !== false &&
                    !error.message?.includes('not configured')
                ) {
                    await new Promise((resolve) => setTimeout(resolve, INITIAL_RETRY_DELAY * Math.pow(2, attempt)));
                    continue;
                }
                throw lastError;
            } finally {
                signal?.removeEventListener('abort', onExternalAbort);
                timeoutController?.cancel();
            }
        }

        throw lastError || new Error(ERROR_MESSAGES.AI_GENERATION_FAILED);
    }

    async requestText({
        prompt,
        systemPrompt,
        model = AI_DEFAULTS.model,
        maxTokens = AI_DEFAULTS.maxTokens,
        temperature = AI_DEFAULTS.temperature,
        inputImages = [],
        signal,
    }) {
        const rawText = await this.requestMessage({
            systemPrompt,
            model,
            maxTokens,
            temperature,
            signal,
            messages: [{role: 'user', content: this.buildUserMessageContent(prompt, inputImages)}],
        });

        return stripCodeFences(rawText || '').trim();
    }

    async requestJson({
        prompt,
        systemPrompt,
        messages,
        model = AI_DEFAULTS.model,
        maxTokens = AI_DEFAULTS.maxTokens,
        temperature = AI_DEFAULTS.temperature,
        inputImages = [],
        signal,
    }) {
        const rawText = await this.requestMessage({
            systemPrompt,
            model,
            maxTokens,
            temperature,
            signal,
            messages:
                messages ||
                [{role: 'user', content: this.buildUserMessageContent(prompt, inputImages)}],
        });

        return this.extractJsonObject(rawText);
    }

    async requestGeneratedContent({
        prompt,
        model = AI_DEFAULTS.model,
        maxTokens = AI_DEFAULTS.maxTokens,
        temperature = AI_DEFAULTS.temperature,
        inputImages = [],
    }) {
        const rawResult = await this.requestJson({
            prompt,
            model,
            maxTokens,
            temperature,
            inputImages,
        });

        return this.normalizeGeneratedResult(rawResult);
    }

    async requestReviewDescription({
        listingContext,
        model = AI_DEFAULTS.model,
        maxTokens = AI_DEFAULTS.maxTokens,
        charLimit = 0,
        profileSettings = null,
        temperature = AI_DEFAULTS.temperature,
        inputImages = [],
    }) {
        const effectiveCharLimit =
            charLimit > 0 ? charLimit : profileSettings?.description?.maxChars || 0;
        const validInputImages = normalizeInputImages(inputImages);

        if (API_CONFIG.ENABLE_AI_FALLBACK) {
            return this.createFallbackReviewDescription({
                listingContext,
                charLimit: effectiveCharLimit,
            });
        }

        const rawResult = await this.requestJson({
            prompt: this.buildReviewDescriptionPrompt({
                listingContext,
                charLimit: effectiveCharLimit,
                profileSettings,
                referenceImageCount: validInputImages.length,
            }),
            model,
            maxTokens,
            temperature,
            inputImages: validInputImages,
        });

        return this.normalizeReviewDescriptionResult(rawResult);
    }
}

const aiService = new AIService();

export default aiService;

export const DEFAULT_MODEL = AI_DEFAULTS.model;
export const buildPrompt = (options) => aiService.buildPrompt(options);
export const createFallbackGeneratedResult = (options) =>
    aiService.createFallbackGeneratedResult(options);
export const extractJsonObject = (raw) => aiService.extractJsonObject(raw);
export const buildProfileRequirements = (options) => aiService.buildProfileRequirements(options);
export const normalizeGeneratedResult = (rawResult) => aiService.normalizeGeneratedResult(rawResult);
export const requestGeneratedContent = (options) => aiService.requestGeneratedContent(options);
export const requestJson = (options) => aiService.requestJson(options);
export const requestText = (options) => aiService.requestText(options);
export const requestReviewDescription = (options) => aiService.requestReviewDescription(options);
export const buildReviewDescriptionPrompt = (options) =>
    aiService.buildReviewDescriptionPrompt(options);
export const buildUserMessageContent = (prompt, inputImages = []) =>
    aiService.buildUserMessageContent(prompt, inputImages);
export const createFallbackReviewDescription = (options) =>
    aiService.createFallbackReviewDescription(options);
export const normalizeReviewDescriptionResult = (rawResult) =>
    aiService.normalizeReviewDescriptionResult(rawResult);
